import type { FC } from 'react';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import Edit02 from '@untitled-ui/icons-react/build/esm/Edit02';
import Box from "@mui/material/Box";
import useHousehold from "src/contexts/household-context";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { usePeopleRolesStore } from "src/hooks/snug/use-peopleRoles";
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';
import { paths } from "src/paths";

interface HouseholdSnapshotSmallProps {
  handleOpen: () => void;
}

const probateThresholds: Record<string, string> = {
  AL: `under $25,000`,
  AK: `under $50,000`,
  AZ: `under $175,000`,
  AR: `under $100,000`,
  CA: `under $184,000`,
  CO: `under $82,000`,
  CT: `under $40,000`,
  DE: `under $30,000`,
  DC: `under $40,000`,
  FL: `under $75,000`,
  GA: `under $10,000`,
  HI: `under $100,000`,
  ID: `under $100,000`,
  IL: `under $100,000`,
  IN: `under $50,000`,
  IA: `under $200,000`,
  KS: `under $75,000`,
  KY: `under $15,000`,
  LA: `under $125,000`,
  ME: `under $40,000`,
  MD: `under $50,000`,
  MA: `under $25,000`,
  MI: `under $25,000`,
  MN: `under $75,000`,
  MS: `under $75,000`,
  MO: `under $40,000`,
  MT: `under $50,000`,
  NE: `under $100,000`,
  NV: `under $25,000`,
  NH: `under $10,000`,
  NJ: `under $20,000`,
  NM: `under $50,000`,
  NY: `under $50,000`,
  NC: `under $20,000`,
  ND: `under $50,000`,
  OH: `under $35,000`,
  OK: `under $200,000`,
  OR: `under $275,000`,
  PA: `under $50,000`,
  RI: `under $15,000`,
  SC: `under $25,000`,
  SD: `under $50,000`,
  TN: `under $50,000`,
  TX: `under $75,000`,
  UT: `under $100,000`,
  VT: `under $45,000`,
  VA: `under $50,000`,
  WA: `under $100,000`,
  WV: `under $150,000`,
  WI: `under $50,000`,
  WY: `under $200,000`,
};

const probateThresholdSeconds: Record<string, string> = {
  AL: `$25k to $1M`,
  AK: `$50k to $1M`,
  AZ: `$175k to $1M`,
  AR: `$100k to $1M`,
  CA: `$184k to $1M`,
  CO: `$82k to $1M`,
  CT: `$40k to $1M`,
  DE: `$30k to $1M`,
  DC: `$40k to $1M`,
  FL: `$75k to $1M`,
  GA: `$10k to $1M`,
  HI: `$100k to $1M`,
  ID: `$100k to $1M`,
  IL: `$100k to $1M`,
  IN: `$50k to $1M`,
  IA: `$200k to $1M`,
  KS: `$75k to $1M`,
  KY: `$15k to $1M`,
  LA: `$125k to $1M`,
  ME: `$40k to $1M`,
  MD: `$50k to $1M`,
  MA: `$25k to $1M`,
  MI: `$25k to $1M`,
  MN: `$75k to $1M`,
  MS: `$75k to $1M`,
  MO: `$40k to $1M`,
  MT: `$50k to $1M`,
  NE: `$100k to $1M`,
  NV: `$25k to $1M`,
  NH: `$10k to $1M`,
  NJ: `$20k to $1M`,
  NM: `$50k to $1M`,
  NY: `$50k to $1M`,
  NC: `$20k to $1M`,
  ND: `$50k to $1M`,
  OH: `$35k to $1M`,
  OK: `$200k to $1M`,
  OR: `$275k to $1M`,
  PA: `$50k to $1M`,
  RI: `$15k to $1M`,
  SC: `$25k to $1M`,
  SD: `$50k to $1M`,
  TN: `$50k to $1M`,
  TX: `$75k to $1M`,
  UT: `$100k to $1M`,
  VT: `$45k to $1M`,
  VA: `$50k to $1M`,
  WA: `$100k to $1M`,
  WV: `$150k to $1M`,
  WI: `$50k to $1M`,
  WY: `$200k to $1M`,
};

/** Net-worth buckets */
export const netWorthOptions: Record<string, string> = {
  "": "",
  "exempt": "",
  "up_to_one": "25k to 1M",
  "up_to_five": "$1M to $5M",
  "up_to_ten": "$5 to $10M",
  "over_ten": "over $10M"
};

/** Marital / partnership status */
export const maritalStatusOptions: Record<string, string> = {
  "": "",
  single: "single",
  married: "married",
  domestic_partnership: "in a Domestic Partnership",
  civil_union: "in a Civil Union"
};

/** Children situation */
export const childrenOptions: Record<string, string> = {
  "": "",
  none: "no children",
  adult: "adult children",
  minor: "minor children"
};

/** Blended-family flag */
export const blendedFamilyOptions: Record<string, string> = {
  "": "",
  blended: "(blended)",
  not: ""
};

/** Estate-plan foundation choice */
export const estatePlanFoundationOptions: Record<string, string> = {
  "": "",
  attorney: "Upload Attorney Documents",
  joint: "Joint Revocable Living Trust",
  trust: "Individual Revocable Living Trust",
  will: "Last Will & Testament"
};

/** State picker (abbreviation → full name) */
export const statesOptions: Record<string, string> = {
  "": "",
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia"
};

export const HouseholdSnapshotSmall: FC<HouseholdSnapshotSmallProps> = (
  props
) => {
  const {
    handleOpen
  } = props;

  const { selectedHousehold, loading: householdLoading } = useHousehold();
  const hOH = selectedHousehold?.head_of_household;
  const household_state_code = hOH?.household_state_code;
  const children = hOH?.children;
  const blended_family = hOH?.blended_family;
  const estate_plan_foundation = hOH?.estate_plan_foundation;
  const net_worth = hOH?.value_of_assets;

  // Get spouse role for marital status
  const { peopleRoles, peopleRolesLoading } = usePeopleRolesStore(hOH?.ud_id || null);
  const spouse_role = peopleRoles?.find((role: any) => role.role === 'SPOUSE');
  const marital_status = spouse_role?.spouse_relationship;
  const spouse_name = spouse_role?.role_target_user_data?.full_name;

  function pickCopy(): string {
    if (net_worth === 'exempt') {                // IF  ➜ use primaryCopy
      return probateThresholds[household_state_code as keyof typeof probateThresholds] || '?';
    } else if (net_worth === 'up_to_one') {         // ELSE IF ➜ use secondaryCopy
      return probateThresholdSeconds[household_state_code as keyof typeof probateThresholdSeconds] || '?';
    }                                // ELSE ➜ use fallbackCopy
    return netWorthOptions[net_worth as keyof typeof netWorthOptions] || '?';
  }

  const navigate = useNavigate();

  return (
    <Card variant="outlined"
      sx={{
        cursor: "pointer",
        borderRadius: 2,
        backgroundColor: "#fff",
        border: '1px solid #aaa',
      }}>
      <CardHeader
        title="Family Snapshot"
        action={(
          <IconButton color="inherit">
            <SvgIcon>
              <Edit02 onClick={handleOpen} />
            </SvgIcon>
          </IconButton>
        )}
        sx={{ pt: 1.5 }}
      />
      {!estate_plan_foundation ?
        <Box sx={{ p: 2, pt: 0 }}>
          <Button variant='contained' fullWidth endIcon={<AutoAwesomeIcon fontSize='small' />} onClick={() => navigate(paths.dashboard.onboarding)}>
            Get your personal plan
          </Button>
        </Box>
        :
        <Box sx={{ pb: 2, px: 3 }}>
          <Typography
            sx={{ display: 'flex', alignItems: 'center' }}
            variant="body2"
          >
            You are {maritalStatusOptions[marital_status as keyof typeof maritalStatusOptions] || '?'}
            {spouse_name ? ` (spouse: ${spouse_name})` : ''}
            {(peopleRolesLoading || householdLoading) && (
              <CircularProgress size={16} sx={{ ml: 1 }} />
            )}, living in {statesOptions[household_state_code as keyof typeof statesOptions] || '?'}. You have {childrenOptions[children as keyof typeof childrenOptions] || '?'}{blended_family && ' (blended family)'} and a net worth of {pickCopy()}. You have chosen a {estatePlanFoundationOptions[estate_plan_foundation as keyof typeof estatePlanFoundationOptions] || '?'} as the foundation of your estate plan.
          </Typography>
        </Box>
      }

    </Card>

  );
};

HouseholdSnapshotSmall.propTypes = {

};
